import React, { useState } from 'react'
import { MdPersonOutline } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import type { Identifier } from '@/hooks/useLoginFlow'
import type { ValidationErrors } from '@/types'

/**
 * Step 1 of the login flow.
 *
 * One field: the customer types their email, mobile number or customer number
 * and we work out which it is (FEDEV-528). Laura's ask, and it removes the
 * "which am I?" decision the three-card version put in front of every
 * customer. The classification lives in classifyIdentifier; this component
 * just collects the string and hands it back.
 *
 * Submits to /auth/check-identity, which decides which Step 2 to render.
 */

interface Props {
  identifier: Identifier
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onIdentifierChange: (value: string) => void
  onSubmit: () => void
}

function LoginIdentifierStep({
  identifier,
  loading,
  apiError,
  fieldErrors,
  onIdentifierChange,
  onSubmit,
}: Props): React.JSX.Element {
  const [error, setError] = useState<ValidationErrors>({})

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (identifier.value.trim() === '') {
      setError({ identifier: 'Please enter your email address, mobile number or customer number' })
      return
    }
    setError({})
    onSubmit()
  }

  const canContinue = identifier.value.trim() !== ''

  return (
    <div className="login-step">
      <h1 className="login-step-heading">Welcome to your customer portal</h1>

      <ValidationSummary errors={fieldErrors} message={apiError} />

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="identifier" className="login-identifier-label">
            Enter your <strong>email address</strong>,{' '}
            <strong>mobile number</strong> or <strong>customer number</strong> to
            sign in.
          </label>
          <div
            className={`login-identifier-field ${error.identifier ? 'is-invalid' : ''}`}
          >
            <span className="login-identifier-icon" aria-hidden="true">
              <MdPersonOutline size={22} />
            </span>
            <input
              id="identifier"
              name="identifier"
              type="text"
              // No type=email/tel: the field accepts any of the three, so a
              // stricter type would fight the customer's autofill and keyboard.
              inputMode="text"
              autoComplete="username"
              autoFocus
              className="login-identifier-input"
              value={identifier.value}
              onChange={(e) => {
                onIdentifierChange(e.target.value)
                if (error.identifier) setError({})
              }}
              disabled={loading}
              aria-label="Email address, mobile number or customer number"
            />
          </div>
          {error.identifier && (
            <span className="form-text is-invalid">{error.identifier}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={loading || !canContinue}
        >
          {loading ? 'Checking…' : 'Continue'}
        </button>
      </form>
    </div>
  )
}

export default LoginIdentifierStep
